'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const floatingCards = [
{
  id: 'card-polish',
  className: 'absolute top-[12%] right-[3%] w-52 float-a',
  icon: '✨',
  label: 'Korekta Lakieru',
  sub: 'Głęboki połysk',
  delay: '0s'
},
{
  id: 'card-interior',
  className: 'absolute bottom-[28%] right-[8%] w-48 float-b',
  icon: '🪑',
  label: 'Pranie Tapicerki',
  sub: 'Jak nowa',
  delay: '1s'
},
{
  id: 'card-coating',
  className: 'absolute top-[40%] left-[2%] w-44 float-c',
  icon: '🛡️',
  label: 'Powłoka Ochronna',
  sub: 'Ceramiczna',
  delay: '2s'
}];


export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 10;
      const cards = heroRef.current.querySelectorAll<HTMLElement>('.parallax-card');
      cards.forEach((card, i) => {
        const depth = (i + 1) * 0.4;
        card.style.transform = `translate(${x * depth}px, ${y * depth}px)`;
      });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-kd-black noise-bg"
      id="hero">
      
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1726259578714-90b92c07873f"
          alt="Czarny samochód sportowy błyszczący po detailingu w profesjonalnym warsztacie"
          fill
          className="object-cover opacity-25"
          priority />
        
        {/* Multi-stop gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-kd-black via-kd-black/90 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-kd-black via-transparent to-kd-black/60" />
        {/* Cyan radial glow */}
        <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] bg-gradient-radial-cyan opacity-50 blur-3xl pointer-events-none" />
      </div>

      {/* Scan line */}
      <div className="scan-overlay z-10" />

      {/* Main content */}
      <div className="relative z-20 max-w-container mx-auto px-6 w-full pt-28 pb-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-kd-cyan/30 bg-kd-cyan/5 mb-8 animate-fade-up"
            style={{ animationFillMode: 'both' }}>
            
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-kd-cyan opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-kd-cyan" />
            </span>
            <span className="text-kd-cyan text-xs font-body font-semibold uppercase tracking-widest">
              Siepraw, Małopolska
            </span>
          </div>

          {/* Headline */}
          <h1
            className="font-heading font-black uppercase leading-none mb-6 animate-fade-up delay-100"
            style={{ animationFillMode: 'both', fontSize: 'clamp(3.2rem, 9vw, 8.5rem)' }}>
            
            <span className="block text-white">UMYJEMY</span>
            <span className="block gradient-text glow-text">TWOJE AUTO</span>
            <span className="block text-white">DO PERFEKCJI</span>
          </h1>

          {/* Sub */}
          <p
            className="text-kd-muted font-body text-lg md:text-xl max-w-lg leading-relaxed mb-10 animate-fade-up delay-200"
            style={{ animationFillMode: 'both' }}>
            
            Profesjonalny detailing samochodowy — korekta lakieru, pranie tapicerki,
            powłoki ceramiczne. Zadzwoń i umów termin.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-4 animate-fade-up delay-300"
            style={{ animationFillMode: 'both' }}>
            
            <a
              href="tel:+48731655388"
              className="btn-primary relative inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-heading font-bold text-white text-xl uppercase tracking-wider z-10">
              
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              731 655 388
            </a>
            <a
              href="#uslugi"
              className="btn-outline inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-heading font-bold text-white text-xl uppercase tracking-wider">
              
              Nasze Usługi
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </a>
          </div>

          {/* Trust bar */}
          <div
            className="flex flex-wrap gap-6 mt-12 animate-fade-up delay-400"
            style={{ animationFillMode: 'both' }}>
            
            {[
            { v: '100%', l: 'Rekomendacji' },
            { v: '14', l: 'Recenzji' },
            { v: '5★', l: 'Ocena' }].
            map((s) =>
            <div key={s.l} className="flex items-center gap-2">
                <span className="font-heading font-black text-kd-cyan text-2xl">{s.v}</span>
                <span className="text-kd-muted text-sm font-body">{s.l}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating cards — desktop only */}
      <div className="hidden lg:block">
        {floatingCards.map((card) =>
        <div
          key={card.id}
          className={`${card.className} parallax-card z-20 transition-transform duration-700 ease-out`}>
          
            <div className="glass-card rounded-2xl p-4 glow-cyan">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{card.icon}</span>
                <div>
                  <p className="font-heading font-bold text-white text-sm uppercase tracking-wide">
                    {card.label}
                  </p>
                  <p className="text-kd-cyan text-xs font-body">{card.sub}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-kd-muted text-xs font-body tracking-widest uppercase">Przewiń</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(58,164,222,0.6)" strokeWidth="2">
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
      </div>
    </section>);

}